import type { ShaclViolation } from '@cogo/kg-browser'
import type { AppValidationReport } from '@/features/design-system/validator/types'

export interface ViolationLite {
  blockId: string
  blockName: string
  gate: 'structural' | 'binding'
  /** '' when the violation carries no focus binding — render/dispatch nothing
   *  rather than a placeholder token (F18). */
  elementId: string
  elementType: string
  ruleId: string
  message: string
  severity: ShaclViolation['severity']
}

// Blocking entries ('violation') sort before advisory ones — within a rank,
// document order (block × phase × violation) is preserved.
const SEVERITY_RANK: Record<ShaclViolation['severity'], number> = {
  violation: 0,
  warning: 1,
  info: 2,
}

/**
 * Task 7 (slice 1) — whether `selectViolations` can safely walk `report`
 * without throwing. `report.blocks`, each `block.phases`, and each
 * `phases[gate].violations` were previously walked unguarded: a malformed
 * report (e.g. a block with no `phases` at all — measured, a real payload
 * that reaches this path) threw a TypeError INSIDE
 * `useDispatchValidationBridge`'s effect, which React's root `ErrorBoundary`
 * then caught, unmounting the whole Studio shell (chat dock included) that
 * effect runs in. Exported so the caller (the bridge) can tell "no
 * violations" (a real, empty report) from "unreadable" (a report whose
 * shape this function refuses to trust) — those are different facts and the
 * caller must not conflate them into the same silent zero.
 */
export function isReadableReport(report: AppValidationReport | null): boolean {
  if (!report || !Array.isArray((report as { blocks?: unknown }).blocks)) return false
  return (report.blocks as unknown[]).every((block) => {
    const phases = (block as { phases?: Record<string, unknown> })?.phases
    // No separate `Boolean(phases)` check: `phases?.[gate]` and the
    // trailing `?.violations` already both use optional chaining, so a
    // missing `phases` (or a missing single gate) falls through to
    // `Array.isArray(undefined)` === false on its own — a redundant
    // explicit check here would be UNTESTABLE dead weight (mutation-tested:
    // deleting it changes no test outcome).
    return (['structural', 'binding'] as const).every(
      (gate) => Array.isArray((phases?.[gate] as { violations?: unknown })?.violations),
    )
  })
}

/**
 * Flattens an AppValidationReport into per-violation rows across ALL
 * severities, ordered blocking-severity-first then document order.
 * Used by the Validator panel's per-violation list.
 *
 * Returns `[]` for a report `isReadableReport` refuses (see that function's
 * own comment) rather than throwing — the caller decides separately whether
 * an empty result here means "no violations" or "unreadable", via
 * `isReadableReport` itself.
 */
export function selectViolations(report: AppValidationReport | null): ViolationLite[] {
  if (!isReadableReport(report)) return []
  const out: ViolationLite[] = []
  for (const block of (report as AppValidationReport).blocks) {
    for (const gate of ['structural', 'binding'] as const) {
      for (const v of block.phases[gate].violations) {
        out.push({
          blockId: block.blockId,
          blockName: block.blockName,
          gate,
          elementId: v.bindings.focus ?? '',
          elementType: v.focusClass ?? '',
          ruleId: v.shape,
          message: v.message ?? `${v.shape} check failed`,
          severity: v.severity,
        })
      }
    }
  }
  // Array.prototype.sort is stable, so ties keep document order.
  return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
}

/**
 * Top `limit` BLOCKING violations (severity === 'violation'), blocking-first
 * then document order. Feeds the auto-spawned chat bubbles only — on-demand
 * per-violation actions use the uncapped `selectViolations`.
 *
 * Caller computes overflow count as `report.violations - returned.length`.
 */
export function selectTopViolations(
  report: AppValidationReport,
  limit: number,
): ViolationLite[] {
  return selectViolations(report)
    .filter((v) => v.severity === 'violation')
    .slice(0, limit)
}
